import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useListKeyboardNavigation } from '@/shared/hooks/useListKeyboardNavigation';
import type { AutocompleteOption } from '../helpers/types';

type UseAutocompleteProps = {
  /** 現在の値 */
  value?: string;
  /** 値変更時のコールバック */
  onValueChange?: (value: string) => void;
  /** フォーカス離脱時のコールバック */
  onBlur?: () => void;
  /** サジェスト候補 */
  options: AutocompleteOption[];
  /** フォーカス時に候補を表示するか */
  showOnFocus?: boolean;
};

/**
 * Autocomplete コンポーネントの共通ロジックを管理するカスタムフック
 */
export const useAutocomplete = ({
  value,
  onValueChange,
  onBlur,
  options,
  showOnFocus = false,
}: UseAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    if (!value) return '';
    const matched = options.find((opt) => opt.value === value);
    return matched ? matched.fieldLabel : value;
  });

  // 自身が発行した value を追跡し、外部からの変更を検知する
  const lastEmittedValueRef = useRef(value);

  // 外部からの value 変更（form.reset() 等）を検知して inputValue を同期
  useEffect(() => {
    if (value === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = value;

    if (!value) {
      setInputValue('');
      return;
    }
    const matched = options.find((opt) => opt.value === value);
    setInputValue(matched ? matched.fieldLabel : value);
  }, [value, options]);

  // フィルタリング: label の部分一致（大文字小文字無視）
  const filteredOptions =
    inputValue.trim() !== ''
      ? options.filter((option) =>
          (option.filterLabel ?? option.fieldLabel)
            .toLowerCase()
            .includes(inputValue.toLowerCase()),
        )
      : options;

  // 候補選択時
  const handleSelectOption = (option: AutocompleteOption) => {
    setInputValue(option.fieldLabel);
    lastEmittedValueRef.current = option.value;
    onValueChange?.(option.value);
    setOpen(false);
  };

  // キーボードナビゲーション
  const {
    activeIndex,
    handleKeyDown: listKeyDown,
    resetActiveIndex,
  } = useListKeyboardNavigation({
    itemCount: filteredOptions.length,
    onSelect: (index) => {
      const option = filteredOptions[index];
      if (option) {
        handleSelectOption(option);
      }
    },
    onClose: () => {
      setOpen(false);
    },
    enabled: open,
  });

  // テキスト入力時（Input の clear ボタン経由のイベントも含む）
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    resetActiveIndex();
    lastEmittedValueRef.current = newValue;
    onValueChange?.(newValue);
    setOpen(newValue.trim() !== '');
  };

  // Popover 開閉（Radix の外部クリック等）
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetActiveIndex();
      onBlur?.();
    }
  };

  // フォーカス時に候補を表示（showOnFocus が true の場合のみ）
  const handleFocus = () => {
    if (showOnFocus && options.length > 0) {
      setOpen(true);
    }
  };

  // Autocomplete 固有: 閉じている時に ArrowDown/Up で Popover を開く
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    listKeyDown(e);
  };

  return {
    open,
    inputValue,
    filteredOptions,
    activeIndex,
    handleInputChange,
    handleSelectOption,
    handleOpenChange,
    handleFocus,
    handleKeyDown,
  };
};
